// Recovery for the one failure that lazy routes plus content-hashed assets
// plus a service worker will eventually produce.
//
// The sequence: a tab is open on build A. Build B deploys and every
// `/assets/*` filename changes. The user navigates, React calls the lazy
// import for a chunk whose URL only existed in build A, and the fetch 404s.
// React renders nothing and the app is a white screen until a manual reload.
//
// A reload fixes it, because the shell is network-first (see public/sw.js) so
// the next navigation picks up build B's asset URLs. The only hazard is a
// reload loop when the failure is NOT staleness — a genuinely broken deploy,
// an offline device. Hence the one-shot flag: exactly one automatic reload per
// session, after which the error boundary is allowed to show the real failure.

const FLAG = "character-studio:chunk-reload";

const CHUNK_FAILURE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

function reloadOnce(): void {
  // sessionStorage throws outright in some privacy modes. An unreadable flag
  // means attempts cannot be counted, so the safe answer is to never reload.
  try {
    if (sessionStorage.getItem(FLAG) !== null) return;
    sessionStorage.setItem(FLAG, "1");
  } catch {
    return;
  }
  window.location.reload();
}

export function installChunkReloadGuard(): void {
  if (typeof window === "undefined") return;

  // Vite's own signal for a failed module preload — it fires before the import
  // rejection in most browsers, so it is the earliest hook available.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnce();
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    if (CHUNK_FAILURE.test(message)) reloadOnce();
  });
}

/**
 * Clear the one-shot flag once the app has mounted and rendered.
 *
 * Without this the guard is single-use for the lifetime of the tab: a user who
 * hit one stale chunk on Monday would get a white screen instead of a recovery
 * on Tuesday's deploy.
 */
export function chunkReloadGuardSettled(): void {
  try {
    sessionStorage.removeItem(FLAG);
  } catch {
    /* Unreadable storage already means the guard never fires. */
  }
}
